import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/skeleton';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth/AuthProvider';
import { useCalendarAvailability } from '@features/booking/hooks/useCalendarAvailability';
import { useWaitingListHours } from '@features/booking/hooks/useWaitingListHours';
import type { ServiceType } from '@features/booking/state/types';
import { sanitizeHtml } from '@shared/utils/sanitize';

const ANY_SERVICE = '-1';
const FLEXIBLE_HOUR = '-1';

const WaitingListFormPage = () => {
  const { t, i18n } = useTranslation('booking');
  const navigate = useNavigate();
  const { goToNext } = useBookingFlow();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();

  useEffect(() => {
    if (booking.isLoading) return;
    if (!booking.selectedDate || booking.diners <= 0) {
      navigate('/', { replace: true });
    }
  }, [booking.isLoading, booking.selectedDate, booking.diners, navigate]);

  const allServiceTypes = (restaurant.serviceTypes ?? []) as ServiceType[];

  // Fetch calendar for all services to filter out services with no real availability
  const { mergedCalendar } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: booking.diners,
    serviceTypes: allServiceTypes,
    enabled: booking.diners > 0,
    meitreNetworkMenuId: booking.meitreNetworkMenuId,
    hasPreferredAccess: !!authState?.preferred,
  });

  // Filter: only show services that have at least one day != not-available-closed
  const serviceTypes = useMemo(() => {
    if (mergedCalendar.length === 0) return allServiceTypes;
    const servicesWithAvailability = new Set<string>();
    for (const day of mergedCalendar) {
      for (const st of day.availableServiceTypes) {
        servicesWithAvailability.add(st);
      }
    }
    const filtered = allServiceTypes.filter(st => servicesWithAvailability.has(st));
    return filtered.length > 0 ? filtered : allServiceTypes;
  }, [allServiceTypes, mergedCalendar]);
  const [selectedService, setSelectedService] = useState<string>(
    booking.wlServiceType ?? (serviceTypes.length === 1 ? ANY_SERVICE : ''),
  );
  const [selectedHour, setSelectedHour] = useState<string>(booking.wlPreferredTime ?? FLEXIBLE_HOUR);
  const [selectedMenus, setSelectedMenus] = useState<number[]>(booking.wlMenus);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const hoursFetchServices = useMemo<ServiceType[]>(() => {
    if (selectedService && selectedService !== ANY_SERVICE) {
      return [selectedService as ServiceType];
    }
    return serviceTypes;
  }, [selectedService, serviceTypes]);

  const { hours: hourOptions, isLoading: isLoadingHours } = useWaitingListHours({
    restaurantId: restaurant.id,
    partySize: booking.diners,
    date: booking.selectedDate,
    serviceTypes: hoursFetchServices,
    enabled: booking.diners > 0 && !!booking.selectedDate,
    meitreNetworkMenuId: booking.meitreNetworkMenuId,
    areaFilterIds: booking.areaFilterIds,
    dedupByHourOnly: !selectedService || selectedService === ANY_SERVICE,
  });

  useEffect(() => {
    if (isLoadingHours) return;
    if (selectedHour === FLEXIBLE_HOUR) return;
    const isValid = hourOptions.some((h) => h.value === selectedHour);
    if (!isValid) setSelectedHour(FLEXIBLE_HOUR);
  }, [isLoadingHours, hourOptions, selectedHour]);

  // Get menus from raw center slots (set by handleJoinWaitingList), fallback to restaurant config.
  // Enriches with party size limits from restaurant config and filters by party size.
  // Matches legacy meitre-frontend behavior: uses raw slots (including type "No") so menus
  // are available even when all processed slots were filtered out.
  const menus: { id: number; name: string; description?: string; isDisabled: boolean; eventMenu: boolean; orderPriority: number }[] = useMemo(() => {
    const lang = i18n.language || 'en';
    const restaurantMenus = restaurant.menus?.[lang] ?? restaurant.menus?.['en'] ?? [];
    const restaurantMenuArray = Array.isArray(restaurantMenus) ? restaurantMenus : [];
    const restaurantMenuMap = new Map(restaurantMenuArray.map((m) => [m.id, m]));

    // 1. Use menus extracted from raw center slots (primary source)
    let slotMenus = booking.wlSlotMenus ?? [];
    // 2. Fallback: restaurant config menus (language-aware)
    // NOTE: Known limitation — may show menus not offered for this date/service/hour.
    // Proper fix requires a backend endpoint for WL-eligible menus.
    if (slotMenus.length === 0) {
      slotMenus = restaurantMenuArray.map((m) => ({
        id: m.id, name: m.name, eventMenu: !!m.eventMenu, orderPriority: m.orderPriority ?? 0,
      }));
    }

    const partySize = booking.diners;

    return slotMenus
      .map((m) => {
        const cfg = restaurantMenuMap.get(m.id);
        const min = cfg?.minPartySizeAllowed ?? 0;
        const max = cfg?.maxPartySizeAllowed ?? 0;
        const isDisabled = (min > 0 && partySize < min) || (max > 0 && partySize > max);
        return { ...m, description: cfg?.description, isDisabled };
      })
      .sort((a, b) => {
        if (a.eventMenu && !b.eventMenu) return -1;
        if (!a.eventMenu && b.eventMenu) return 1;
        return a.orderPriority - b.orderPriority;
      });
  }, [booking.wlSlotMenus, booking.diners, restaurant.menus, i18n.language]);

  const enabledMenus = menus.filter((m) => !m.isDisabled);

  // Auto-select when only one enabled menu
  useEffect(() => {
    if (enabledMenus.length === 1 && selectedMenus.length === 0) {
      setSelectedMenus([enabledMenus[0].id]);
    }
  }, [enabledMenus.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMenu = (id: number) => {
    setSelectedMenus((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );
  };

  const handleContinue = () => {
    setHasSubmitted(true);
    if (serviceTypes.length > 1 && !selectedService) return;

    const effectiveService = selectedService === ANY_SERVICE ? null : selectedService as ServiceType;
    const effectiveHour = selectedHour === FLEXIBLE_HOUR ? null : selectedHour;

    booking.setWlPreferredTime(effectiveHour);
    booking.setWlMenus(selectedMenus);
    booking.setWlServiceType(effectiveService);
    goToNext();
  };

  return (
    <BookingPageLayout
      title={t('JOIN_THE_WAITING_LIST')}
      subtitle={t('WAITING_LIST_FORM_SUBTITLE')}
    >
      <div className="w-full px-6 space-y-6">
        {/* Service selector */}
        {serviceTypes.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('WAITING_LIST_SERVICE')}
            </label>
            <div className="relative">
              <select
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setSelectedHour(FLEXIBLE_HOUR);
                }}
                className={`w-full rounded-xl border bg-card px-4 py-3 pr-10 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand appearance-none ${
                  hasSubmitted && !selectedService ? 'border-red-500' : 'border-border'
                }`}
              >
                <option value="" disabled>
                  {t('WAITING_LIST_SELECT_SERVICE')}
                </option>
                <option value={ANY_SERVICE}>
                  {t('NEW_MODAL_ANY_SERVICE')}
                </option>
                {serviceTypes.map((st) => (
                  <option key={st} value={st}>
                    {t(`FILTER_STRING_${st.toUpperCase()}`, st)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none" size={18} />
            </div>
          </div>
        )}

        {/* Hour selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground/70">
            {t('CALENDAR_DIALOG_WAITING_LIST_TEXT_HOUR')}
          </label>
          {isLoadingHours ? (
            <Skeleton className="h-[50px] w-full rounded-xl" />
          ) : (
            <div className="relative">
              <select
                value={selectedHour}
                onChange={(e) => setSelectedHour(e.target.value)}
                className="w-full rounded-xl border border-border bg-card px-4 py-3 pr-10 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand appearance-none"
              >
                <option value={FLEXIBLE_HOUR}>
                  {t('NEW_MODAL_FLEXIBLE')}
                </option>
                {hourOptions.map((h) => (
                  <option key={`${h.serviceType}-${h.value}`} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none" size={18} />
            </div>
          )}
        </div>

        {/* Menu selection — multiple enabled menus */}
        {enabledMenus.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground/70">
              {t('WAITING_LIST_SELECT_MENUS')}
            </label>
            <div className="space-y-2">
              {enabledMenus.map((menu) => (
                <label
                  key={menu.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                    selectedMenus.includes(menu.id)
                      ? 'border-brand bg-brand/5'
                      : 'border-border hover:border-brand-light'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMenus.includes(menu.id)}
                    onChange={() => toggleMenu(menu.id)}
                    className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
                  />
                  <span className="text-sm text-foreground">{menu.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Single enabled menu — read-only display with description */}
        {enabledMenus.length === 1 && (
          <div className="bg-foreground/5 rounded-xl p-4">
            <p className="text-base font-bold text-foreground">{enabledMenus[0].name}</p>
            {enabledMenus[0].description && (
              <div
                className="text-sm text-foreground/60 mt-1"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(enabledMenus[0].description) }}
              />
            )}
          </div>
        )}
      </div>

      <div className="w-full px-6 mt-8">
        <Button
          data-testid="continue-btn"
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={handleContinue}
        >
          {t('WAITING_LIST_CONTINUE')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default WaitingListFormPage;
